// Shared primitives. Pages compose these rather than styling their own boxes,
// which is what keeps every screen visually identical.
import React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, Inbox } from 'lucide-react';

/* ── Page scaffolding ──────────────────────────────────────────────────── */

export const Page = ({ children }) => <div className="page">{children}</div>;

export const PageHead = ({ title, children, actions }) => (
  <header className="page-head">
    <div>
      <h1>{title}</h1>
      {children && <p>{children}</p>}
    </div>
    {actions && <div className="row">{actions}</div>}
  </header>
);

/* ── Card ──────────────────────────────────────────────────────────────── */

export const Card = ({ title, sub, actions, children, flush, className = '', ...rest }) => (
  <section className={`card ${className}`} {...rest}>
    {(title || actions) && (
      <div className="card-head">
        <div className="grow">
          {title && <h2>{title}</h2>}
          {sub && <div className="sub">{sub}</div>}
        </div>
        {actions && <div className="row-tight">{actions}</div>}
      </div>
    )}
    <div className={`card-body${flush ? ' flush' : ''}`}>{children}</div>
  </section>
);

export const Stat = ({ label, value, unit, note, tone }) => (
  <div className="card stat">
    <div className="stat-k">{label}</div>
    <div className="stat-v" style={tone ? { color: `var(--${tone})` } : undefined}>
      <span className="num">{value}</span>
      {unit && <small>{unit}</small>}
    </div>
    {note && <div className="stat-n">{note}</div>}
  </div>
);

/* ── Controls ──────────────────────────────────────────────────────────── */

export const Button = ({ variant = 'ghost', size, icon: Icon, children, loading, ...rest }) => (
  <button
    className={`btn btn-${variant}${size ? ` btn-${size}` : ''}${!children ? ' btn-icon' : ''}`}
    {...rest}
  >
    {loading ? <span className="spinner" /> : Icon && <Icon size={size === 'sm' ? 13 : 15} />}
    {children}
  </button>
);

export const Field = ({ label, hint, children }) => (
  <label className="field">
    {label && <span>{label}</span>}
    {children}
    {hint && <span className="hint">{hint}</span>}
  </label>
);

export const Input = ({ numeric, ...rest }) => (
  <input className={`input${numeric ? ' num-input' : ''}`} {...rest} />
);

export const Select = ({ options, ...rest }) => (
  <select className="select" {...rest}>
    {options.map((o) =>
      typeof o === 'string'
        ? <option key={o} value={o}>{o}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>
    )}
  </select>
);

export const Textarea = (props) => <textarea className="textarea" {...props} />;

export const Tabs = ({ value, onChange, options }) => (
  <div className="tabs" role="tablist">
    {options.map((o) => {
      const val = typeof o === 'string' ? o : o.value;
      const label = typeof o === 'string' ? o : o.label;
      return (
        <button
          key={val}
          role="tab"
          aria-selected={value === val}
          className={`tab${value === val ? ' active' : ''}`}
          onClick={() => onChange(val)}
        >
          {label}
        </button>
      );
    })}
  </div>
);

/* ── Feedback ──────────────────────────────────────────────────────────── */

export const Badge = ({ tone = 'muted', title, children }) => (
  <span className={`badge badge-${tone}`} title={title}>{children}</span>
);

const ALERT_ICON = { crit: AlertCircle, warn: AlertTriangle, ok: CheckCircle2, info: Info };

export const Alert = ({ tone = 'info', children }) => {
  const Icon = ALERT_ICON[tone] || Info;
  return (
    <div className={`alert alert-${tone}`} role={tone === 'crit' ? 'alert' : undefined}>
      <Icon size={15} />
      <div className="grow">{children}</div>
    </div>
  );
};

export const Empty = ({ icon: Icon = Inbox, title, children }) => (
  <div className="empty">
    <Icon size={26} />
    {title && <div className="empty-title">{title}</div>}
    {children && <p>{children}</p>}
  </div>
);

export const Loading = ({ label = 'Loading…' }) => (
  <div className="empty">
    <span className="spinner" />
    <div className="empty-title">{label}</div>
  </div>
);

export const Bar = ({ value, max, color = 'var(--accent)' }) => (
  <div className="bar">
    <span style={{ width: `${Math.min(100, max > 0 ? (value / max) * 100 : 0)}%`, background: color }} />
  </div>
);

export const Disclaimer = ({ children }) => <p className="disclaimer">{children}</p>;
